import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { useAbility } from '@casl/react';
import { ComponentDataTable, SimpleCenteredModal, DefaultModal, Icon, ButtonTooltip } from '../../index';
import { indexEmployeeCourseRequest, exportEmployeeCourseRequest } from '../../../requests/employeeCourses';
import getColumns from './TalentColumns';
import { downloadFile } from '../../../services/utils';
import DocumentPreview from '../../../screens/DocumentPreview/DocumentPreview';
import { AbilityContext } from '../../../config/abilityContext';

const TalentDataTable = ({ employeeId }) => {
  const dispatch = useDispatch();
  const ability = useAbility(AbilityContext);
  const [employeeCourses, setEmployeeCourses] = useState([]);
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({});
  const [documentModalShow, setDocumentModalShow] = useState(false);
  const [documentModalBody, setDocumentModalBody] = useState('');

  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalBody, setModalBody] = useState('');
  const [modalAction, setModalAction] = useState(() => null);

  const defaultDates = useMemo(
    () => ({
      start: moment().startOf('year'),
      end: moment().endOf('year')
    }),
    []
  );

  const handleButtonClick = (item, action) => {
    switch (action) {
      case 'show':
      case 'read':
        setDocumentModalBody(<DocumentPreview documentId={item.certificateDocumentId} employeeId={item.employeeId} />);
        setDocumentModalShow(true);
        break;
      default:
        break;
    }
  };

  const columns = useMemo(() => getColumns(handleButtonClick), []);

  const handleIndexRequest = params => {
    setLoading(true);
    const requestParams = {
      ...params,
      filter_employee_id: employeeId
    };
    setQuery(requestParams);
    indexEmployeeCourseRequest({
      dispatch,
      params: requestParams,
      successCallback: res => {
        const responseData = camelCaseRecursive(res.data);
        const { data, metadata } = responseData;

        setEmployeeCourses(data || []);
        setAmount(metadata?.amount || 0);
      },
      callback: () => setLoading(false)
    });
  };

  const handleDownload = () => {
    setLoading(true);
    exportEmployeeCourseRequest({
      dispatch,
      params: query,
      responseType: 'text',
      successCallback: res => {
        setModalShow(false);
        downloadFile(res);
      },
      callback: () => setLoading(false)
    });
  };

  const handleMassAction = action => {
    if (action === 'export') {
      setModalTitle('Exportar capacitaciones');
      setModalBody('¿Estás seguro que deseas descargar el listado de capacitaciones?');
      setModalAction(() => handleDownload);
      setModalShow(true);
    }
  };

  const handleDocumentModalClose = () => {
    setDocumentModalShow(false);
  };

  const handleModalClose = () => {
    setModalShow(false);
  };

  return (
    <div className="mx-3 mt-3">
      <div className="data-table-yellow">
        <ComponentDataTable
          resourceRequest={handleIndexRequest}
          onRequest={loading}
          columns={columns}
          data={employeeCourses}
          totalRows={amount}
          noDataComponentText="No hay capacitaciones registradas en este periodo."
          withDateSelect
          withStartDate
          withEndDate
          defaultStartDate={defaultDates.start}
          defaultEndDate={defaultDates.end}
          withMassActions
          massActions={
            <>
              {ability.can('export', 'EmployeeCourse') && (
                <ButtonTooltip
                  variant="circle-primary"
                  className="mr-2 mb-3 btn-circle"
                  text="Exportar"
                  onClick={() => handleMassAction('export')}
                >
                  <Icon icon="download-2" />
                </ButtonTooltip>
              )}
            </>
          }
        />
      </div>

      <SimpleCenteredModal
        body={documentModalBody}
        onHide={handleDocumentModalClose}
        show={documentModalShow}
        size="xl"
      />

      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={handleModalClose}
        handleConfirm={() => modalAction()}
        disabled={loading}
        variantBtnClose="outline-info"
        variantBtnSave="primary"
      />
    </div>
  );
};

export default TalentDataTable;
